import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FirebaseError } from 'firebase/app';
import { useEffect, useState } from 'react';
import loginIllustration from '~/assets/login-illustratie.png';
import logo from '~/assets/Logo-mila.png';
import { signInWithEmailPassword } from '~/lib/auth';
import { auth } from '~/lib/firebase';
import { Button } from '~/components/ui/button';
import {
    Field,
    FieldError,
    FieldGroup,
    FieldLabel,
} from '~/components/ui/field';
import { Input } from '~/components/ui/input';

// Zelfde opmaak als de zoekbox in de kopbalk (header-global-search.tsx).
const FIELD_CLASS =
    'h-7 rounded-lg border-[#F0F0F0] bg-[#F8F8F8] px-3 py-0 text-[12px] text-[#202020] placeholder:text-[#8C8C8C] md:text-[12px] focus-visible:border-[#F0F0F0] focus-visible:ring-2 focus-visible:ring-primary/20';

export const Route = createFileRoute('/login')({
    component: LoginPage,
});

function authErrorMessage(error: unknown): string {
    if (error instanceof FirebaseError) {
        switch (error.code) {
            case 'auth/invalid-credential':
            case 'auth/user-not-found':
            case 'auth/wrong-password':
                return 'Incorrect email or password.';
            case 'auth/too-many-requests':
                return 'Too many attempts. Try again later.';
            case 'auth/password-does-not-meet-requirements':
                return 'Password does not meet requirements.';
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            default:
                return error.message || 'Authentication failed';
        }
    }
    if (error instanceof Error) return error.message;
    return 'Authentication failed';
}

function LoginPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                await auth.authStateReady();
                if (auth.currentUser) {
                    navigate({ to: '/' });
                    return;
                }
            } catch (e) {
                setError(authErrorMessage(e));
            } finally {
                setCheckingAuth(false);
            }
        })();
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!email.includes('@')) {
            setError('Please enter a valid email address.');
            return;
        }

        setSubmitting(true);
        try {
            await signInWithEmailPassword(email, password);
            navigate({ to: '/' });
        } catch (e) {
            setError(authErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (checkingAuth) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-white">
                <p className="text-[12px] text-muted-foreground">Loading...</p>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen bg-white p-4">
            <div className="flex flex-1 items-center justify-center px-8 py-16">
            <form onSubmit={handleSubmit} className="w-[260px]">
                <FieldGroup className="flex flex-col items-start gap-6">
                    <img src={logo} alt="Mila" className="h-9 w-auto" />
                    <div className="flex w-full flex-col items-start gap-3">
                        <h1 className="text-[14px] font-medium">Inloggen</h1>
                        <Field>
                            <FieldLabel htmlFor="email" className="text-[12px]">Email</FieldLabel>
                            <Input
                                id="email"
                                type="email"
                                className={FIELD_CLASS}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@company.com"
                                autoComplete="email"
                                required
                                disabled={submitting}
                            />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="password" className="text-[12px]">Password</FieldLabel>
                            <Input
                                id="password"
                                type="password"
                                className={FIELD_CLASS}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                                disabled={submitting}
                            />
                            {error && <FieldError className="text-[12px]">{error}</FieldError>}
                        </Field>
                        <Field>
                            <Button
                                type="submit"
                                className="h-7 w-full rounded-lg bg-[#202020] text-[12px] font-medium text-white hover:bg-[#000000]"
                                disabled={submitting}
                            >
                                {submitting ? 'Bezig...' : 'Inloggen'}
                            </Button>
                        </Field>
                    </div>
                </FieldGroup>
            </form>
            </div>
            <div className="hidden flex-1 items-center justify-center overflow-hidden rounded-3xl bg-[#F8F8F8] p-12 lg:flex">
                <img
                    src={loginIllustration}
                    alt="Voorbeeld van hoe Mila een e-mail verwerkt"
                    className="h-auto max-h-full w-[70%] max-w-[340px] object-contain"
                />
            </div>
        </div>
    );
}
